import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@foundation/altzero_base/base/components/ui/button";
import { Input } from "@foundation/altzero_base/base/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@foundation/altzero_base/base/components/ui/dialog";
import { useUser } from "@foundation/altzero_base/base/contextapi/UserContext";
import { useToast } from "@foundation/altzero_base/base/hooks/use-toast";
import { ArrowLeft, Loader2, Save, Copy, FileText, Eye, Pencil } from "lucide-react";
// @ts-ignore
import ModernTemplateEditor from "../../../altzero_plugins/features/template-editor/Editor/features/Editor/components/ModernEditor/ModernTemplateEditor";
import {
  createCustomTemplate,
  updateCustomTemplate,
  loadTemplateContent,
  isEditorTemplate,
} from "../services/documentLibraryService";
import type { TemplateEditorConfig } from "../types/brandWorkflowConfig";
import ScopingAILayout from "../components/ScopingAILayout";

const EDITOR_CONFIG = {
  showToolbar: true,
  showSidebar: true,
  showSaveButton: false,
  enableMultiPage: true,
  enableVariables: true,
  enableExport: false,
  theme: "light",
};

const ProposalTemplateEditor: React.FC<{ config?: TemplateEditorConfig }> = ({ config }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUser();
  const { toast } = useToast();

  const Layout = config?.layout ?? ScopingAILayout;
  const templatesListRoute = config?.routes?.templatesList ?? "/scopingai/resource-library/documents";
  const svcCfg = config?.serviceConfig;

  const isEditMode = Boolean(id);
  const isPreviewMode = searchParams.get("preview") === "true";

  const [templateName, setTemplateName] = useState("New Proposal Template");
  const [editorState, setEditorState] = useState<any>(null);
  const [initialData, setInitialData] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(isEditMode);

  // Save dialog state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveAction, setSaveAction] = useState<"update" | "saveAsNew">(
    isEditMode ? "update" : "saveAsNew"
  );
  const [saveDialogName, setSaveDialogName] = useState("");
  const [isPublic, setIsPublic] = useState(false);

  // Load existing template in edit mode
  useEffect(() => {
    if (!isEditMode || !id) return;

    const load = async () => {
      try {
        setIsLoadingTemplate(true);
        const content = await loadTemplateContent(id, svcCfg);

        if (isEditorTemplate(content)) {
          const parsed = JSON.parse(content);
          const pages = parsed.pages ?? parsed.editorState?.pages ?? [];
          const zoom = parsed.zoom ?? parsed.editorState?.zoom ?? 1;
          setInitialData({
            pages,
            currentPageId: pages?.[0]?.id,
            zoom,
            showGrid: false,
            previewMode: isPreviewMode,
          });
        } else {
          toast({
            title: "Not an editor template",
            description: "This template was not created with the visual editor.",
            variant: "destructive",
          });
          navigate(templatesListRoute);
        }

        // Load the template name from DB using the configured client/table
        const dbClient = svcCfg?.supabaseClient ?? (await import("../utils/supabaseClient")).supabasePublic;
        const tableName = svcCfg?.tableName ?? "scopingai_proposaltemplates";
        const { data } = await (dbClient as any)
          .from(tableName)
          .select("name, is_public")
          .eq("id", id)
          .single();
        if (data?.name) setTemplateName(data.name);
        if (data?.is_public !== undefined) setIsPublic(data.is_public);
      } catch (err) {
        toast({
          title: "Failed to load template",
          description: err instanceof Error ? err.message : "Unknown error",
          variant: "destructive",
        });
        navigate(templatesListRoute);
      } finally {
        setIsLoadingTemplate(false);
      }
    };

    load();
  }, [id, isEditMode, navigate, toast, svcCfg, templatesListRoute]);

  // Open save dialog — validates first, then shows options
  const handleSave = () => {
    if (!user?.id) {
      toast({ title: "Not authenticated", variant: "destructive" });
      return;
    }
    if (!editorState?.pages?.length && !initialData?.pages?.length) {
      toast({
        title: "Nothing to save",
        description: "Add at least one page before saving.",
        variant: "destructive",
      });
      return;
    }
    setSaveDialogName(templateName);
    setSaveAction(isEditMode ? "update" : "saveAsNew");
    setShowSaveDialog(true);
  };

  // Perform the actual save based on selected action
  const handleConfirmSave = async () => {
    const name = saveDialogName.trim();
    if (!name) {
      toast({ title: "Please enter a template name", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      const pages = editorState?.pages ?? initialData?.pages ?? [];
      const content = JSON.stringify({ pages, zoom: editorState?.zoom ?? 1 });

      if (saveAction === "update" && isEditMode && id) {
        await updateCustomTemplate(id, {
          name,
          content,
          category: "Editor Template",
          is_public: isPublic,
        }, svcCfg);
        toast({
          title: "Template updated",
          description: `"${name}" has been saved.`,
        });
      } else {
        await createCustomTemplate({
          name,
          description: "",
          content,
          category: "Editor Template",
          userId: user!.id,
          is_public: isPublic,
        }, svcCfg);
        toast({
          title: "Template saved",
          description: `"${name}" has been saved to your library.`,
        });
      }

      setShowSaveDialog(false);
      navigate(templatesListRoute);
    } catch (err) {
      toast({
        title: "Save failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <div className="flex flex-col h-screen overflow-hidden">
        {/* Fixed Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 shrink-0">
          <Button
            variant="ghost"
            onClick={() => navigate(templatesListRoute)}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Templates
          </Button>

          <div className="flex items-center gap-4">
            {/* Template name — only editable in edit mode */}
            {isPreviewMode ? (
              <span className="text-base font-semibold text-gray-900 px-2 py-1">
                {templateName}
              </span>
            ) : (
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="text-base font-semibold text-gray-900 text-center border-0 border-b border-transparent hover:border-gray-300 focus:border-emerald-500 focus:outline-none bg-transparent px-2 py-1 max-w-xs"
              />
            )}

            {/* Edit / Preview tab toggle */}
            {isEditMode && (
              <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-1">
                <button
                  onClick={() => setSearchParams({})}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    !isPreviewMode
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>
                <button
                  onClick={() => setSearchParams({ preview: "true" })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    isPreviewMode
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Eye className="h-3.5 w-3.5" />
                  Preview
                </button>
              </div>
            )}
          </div>

          {/* Save button — hidden in preview mode */}
          {!isPreviewMode && (
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Template
                </>
              )}
            </Button>
          )}

          {/* In preview mode — show Edit button instead */}
          {isPreviewMode && isEditMode && (
            <Button
              onClick={() => setSearchParams({})}
              variant="outline"
              className="gap-2"
            >
              <Pencil className="h-4 w-4" />
              Edit Template
            </Button>
          )}
        </div>

        {/* Editor */}
        <div className="flex-1 overflow-hidden bg-white">
          {isLoadingTemplate ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
            </div>
          ) : (
            <ModernTemplateEditor
              config={{ ...EDITOR_CONFIG, storageConfig: svcCfg }}
              initialData={initialData ?? undefined}
              onChange={(state: any) => setEditorState(state)}
              className="h-full w-full"
            />
          )}
        </div>
      </div>

      {/* Save Template Dialog */}
      <Dialog
        open={showSaveDialog}
        onOpenChange={(open) => !isSaving && setShowSaveDialog(open)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save Template</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Action selection — only shown when editing an existing template */}
            {isEditMode && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">
                  What would you like to do?
                </p>

                <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="saveAction"
                    value="update"
                    checked={saveAction === "update"}
                    onChange={() => setSaveAction("update")}
                    disabled={isSaving}
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300"
                  />
                  <div className="ml-3 flex items-center gap-2">
                    <Save className="h-4 w-4 text-emerald-600 shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        Update existing template
                      </div>
                      <div className="text-xs text-gray-500">
                        Overwrite the current template with your changes
                      </div>
                    </div>
                  </div>
                </label>

                <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="saveAction"
                    value="saveAsNew"
                    checked={saveAction === "saveAsNew"}
                    onChange={() => setSaveAction("saveAsNew")}
                    disabled={isSaving}
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300"
                  />
                  <div className="ml-3 flex items-center gap-2">
                    <Copy className="h-4 w-4 text-blue-600 shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        Save as new template
                      </div>
                      <div className="text-xs text-gray-500">
                        Create a new template and keep the original
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            )}

            {/* Template name */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Template Name *
              </label>
              <Input
                value={saveDialogName}
                onChange={(e) => setSaveDialogName(e.target.value)}
                placeholder="Enter template name..."
                disabled={
                  isSaving || (saveAction === "update" && isEditMode)
                }
                onKeyDown={(e) =>
                  e.key === "Enter" && !isSaving && saveDialogName.trim() && handleConfirmSave()
                }
                autoFocus={!isEditMode}
              />
              {saveAction === "update" && isEditMode && (
                <p className="text-xs text-gray-500">
                  Name cannot be changed when updating an existing template
                </p>
              )}
            </div>

            {/* Visibility */}
            <div className="flex items-center gap-3">
              <input
                id="isPublic"
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                disabled={isSaving}
                className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 disabled:opacity-50"
              />
              <div>
                <label htmlFor="isPublic" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Make this template public
                </label>
                <p className="text-xs text-gray-500">Public templates are visible to all users</p>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowSaveDialog(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSave}
              disabled={!saveDialogName.trim() || isSaving}
              className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : saveAction === "update" ? (
                <>
                  <Save className="h-4 w-4" />
                  Update Template
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4" />
                  Save as New Template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ProposalTemplateEditor;
